import * as React from 'react';
import qs from 'query-string';
import { useQuery } from '@tanstack/react-query';
import { Tooltip } from 'antd';
import * as Store from '../../store';
import { checkEmptyRule } from '../utils';
import { getComponentConfig, compareRuleValues, getRuleValuesTitle } from './utils';
import RuleValueInput from './Input';
import RuleValueSelect from './Select';
import * as Styled from '../styled';
import type * as Types from '../../../../types';

export interface RuleAtomValueSelectorProps {
  meta?: Types.DimensionFieldMeta & { tagKey?: string | null };
  value: Types.FilterRuleValuePair[];
  disabled?: boolean;
  defaultActive?: boolean;
  isNot?: boolean;
  onChange?: (value: RuleAtomValueSelectorProps['value']) => void;
}

export const RuleAtomValueBlock = (
  props: Pick<RuleAtomValueSelectorProps, 'value' | 'disabled'> &
    React.BaseHTMLAttributes<HTMLSpanElement>,
) => {
  const { value, disabled, ...params } = props;
  const displayedValues = value.slice(0, 3).map((it) => it.display);

  return (
    <Tooltip title={getRuleValuesTitle(value)}>
      <Styled.AtomEditableRuleValue {...params} $disabled={disabled}>
        {displayedValues.map((it, ix) => (
          <React.Fragment key={it}>
            {ix > 0 && <span>or</span>}
            <span>{it}</span>
          </React.Fragment>
        ))}
        {value.length > 3 && <Styled.RestTips>+{value.length - 3}</Styled.RestTips>}
      </Styled.AtomEditableRuleValue>
    </Tooltip>
  );
};

const RuleAtomValueSelector = (props: RuleAtomValueSelectorProps) => {
  const { meta, disabled, defaultActive, isNot = false, value, onChange } = props;
  const onDimensionValueSearch = Store.useOnDimensionValueSearch();
  const [editing, setEditing] = React.useState(defaultActive || checkEmptyRule(value));
  const [search, setSearch] = React.useState('');
  const valueSearchUrl =
    meta?.tagKey && meta.urls.value
      ? qs.stringifyUrl({ url: meta.urls.value, query: { key: meta.tagKey } })
      : meta?.urls.value;
  const searchedDimensionValues = useQuery(
    ['filter-dialog', 'filters', 'dimension', valueSearchUrl, 'search', search],
    async () => {
      return (await onDimensionValueSearch?.(search, valueSearchUrl)) ?? [];
    },
    {
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      enabled: !!valueSearchUrl && search?.length > 2,
    },
  );

  React.useEffect(() => {
    if (checkEmptyRule(value)) setEditing(true);
  }, [value]);

  const handleChange = (v: RuleAtomValueSelectorProps['value']) => {
    if (compareRuleValues(value, v)) return;

    onChange?.(
      v.map((it) => ({
        value: typeof it.value === 'string' ? it.value.trim() : it.value,
        display: it.display.trim(),
      })),
    );
  };

  const handleBlur = () => {
    if (!checkEmptyRule(value)) {
      setEditing(false);
    }
  };

  if (meta && editing) {
    const config = getComponentConfig(meta);

    if (config.type === 'input' && isNot) {
      const inputValue = value[0].value;

      return (
        <RuleValueInput
          disabled={disabled}
          defaultValue={inputValue}
          onChange={(v: string) => handleChange([{ value: v, display: v }])}
          onBlur={handleBlur}
        />
      );
    }

    if (config.type === 'input') {
      return (
        <RuleValueSelect
          disabled={disabled}
          valueEditable
          showSearch
          filterOption={false}
          mode="tags"
          value={value}
          tokenSeparators={['\n', '\t']}
          notFoundContent={null}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      );
    }

    if (config.type === 'static') {
      return (
        <RuleValueSelect
          disabled={disabled}
          mode={isNot ? undefined : 'multiple'}
          showSearch
          value={value}
          options={config.value.map((it) => ({ label: it.display, value: it.value }))}
          optionFilterProp="label"
          onChange={handleChange}
          onBlur={handleBlur}
        />
      );
    }

    const options =
      searchedDimensionValues.data?.map((it) => ({
        label: it.text,
        value: it.value,
      })) || [];

    if (isNot && config.searchBackfill && search && !options.find((it) => it.label === search)) {
      options.unshift({ label: search, value: search });
    }

    return (
      <RuleValueSelect
        disabled={disabled}
        showSearch
        valueEditable={config.searchBackfill}
        loading={searchedDimensionValues.isFetching}
        filterOption={false}
        mode={isNot ? undefined : config.searchBackfill ? 'tags' : 'multiple'}
        tokenSeparators={!isNot && config.searchBackfill ? ['\n', '\t'] : undefined}
        value={value}
        options={options}
        notFoundContent={search ? undefined : null}
        popupRender={(menu) => {
          if (searchedDimensionValues.isFetching) {
            return <Styled.DropdownSpin />;
          }

          return menu;
        }}
        onChange={handleChange}
        onSearch={setSearch}
        onBlur={handleBlur}
      />
    );
  }

  return (
    <RuleAtomValueBlock
      disabled={disabled}
      value={value}
      onClick={disabled ? undefined : () => setEditing(true)}
    />
  );
};

export default React.memo(RuleAtomValueSelector);
